import { useEffect, useRef, useState } from 'react';
import { Spinner, Form } from 'react-bootstrap';
import { loadGoogleMaps } from 'utils/googleMapsLoader';
import { Coordinates } from 'features/location/googleLocation';

interface LocationAutocompleteProps {
  value: string;
  onChange: (location: string) => void;
  onCoordinatesChange: (coords: Coordinates) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  errorMessage?: string;  
}

/**
 * LocationAutocomplete Component
 * Uses Google Maps Places API for location autocomplete
 * Automatically extracts and passes coordinates to parent
 */
export const LocationAutocomplete = ({
  value,
  onChange,
  onCoordinatesChange,
  placeholder = 'Search location...',
  label,
  required = false,
  errorMessage
}: LocationAutocompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isSelectionRef = useRef(false);
  const [localValue, setLocalValue] = useState(value);
  const onChangeRef = useRef(onChange);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);

  useEffect(() => {
    onChangeRef.current = onChange;
    onCoordinatesChangeRef.current = onCoordinatesChange;
  }, [onChange, onCoordinatesChange]);

  useEffect(() => {
    const inputEl = inputRef.current;
    const isFocused = inputEl && document.activeElement === inputEl;

    if (!isFocused && !isSelectionRef.current && value !== localValue) {
      setLocalValue(value);
    }
  }, [value, localValue]);

  useEffect(() => {
    let isMounted = true;

    const initializeAutocomplete = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load Google Maps with Places library
        await loadGoogleMaps();

        if (!inputRef.current || !isMounted) {
          return;
        }

        // Set up PAC styling only once
        const styleId = 'pac-container-styles';
        if (!document.getElementById(styleId)) {
          const style = document.createElement('style');
          style.id = styleId;
          style.innerHTML = `
            .pac-container {
              z-index: 99999 !important;
            }
          `;
          document.head.appendChild(style);
        }

        // Create autocomplete instance
        const autocomplete = new (
          window as any as { google: { maps: { places: { Autocomplete: any } } } }
        ).google.maps.places.Autocomplete(inputRef.current, {
          types: ['geocode'],
          componentRestrictions: { country: 'gb' },
          fields: ['formatted_address', 'geometry', 'place_id']
        });

        autocompleteRef.current = autocomplete;

        // Add place_changed listener
        autocomplete.addListener('place_changed', () => {
          if (!isMounted) return;

          const place = autocomplete.getPlace();

          if (!place.geometry || !place.geometry.location) {
            if (isMounted) setError('Invalid location selected');
            return;
          }

          const coordinates: Coordinates = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };

          const locationName = place.formatted_address || '';

          // Set flag to indicate selection is happening
          isSelectionRef.current = true;

          if (isMounted) {
            setLocalValue(locationName);
            onChangeRef.current(locationName);
            onCoordinatesChangeRef.current(coordinates);
            setError(null);
          }

          // Reset flag after brief delay
          setTimeout(() => {
            isSelectionRef.current = false;
          }, 100);
        });

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          const errorMsg =
            err instanceof Error
              ? err.message
              : 'Failed to load location services';
          setError(errorMsg);
          setIsLoading(false);
          console.error('LocationAutocomplete error:', err);
        }
      }
    };

    initializeAutocomplete();

    return () => {
      isMounted = false;
      if (autocompleteRef.current) {
        (window as any).google?.maps?.event?.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only trigger onChange if not in selection mode
    if (!isSelectionRef.current) {
      const nextValue = e.target.value;
      setLocalValue(nextValue);
      onChangeRef.current(nextValue);
    }
  };

  return (
    <Form.Group className="mb-3">
      {label && (
        <Form.Label>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </Form.Label>
      )}

      <div className="position-relative">
        <Form.Control
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={isLoading}
          isInvalid={!!error || !!errorMessage}
          autoComplete="off"
        />

        {isLoading && (
          <div
            className="position-absolute"
            style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
          >
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>

      {error && (
        <Form.Text className="d-block text-danger mt-2">{error}</Form.Text>
      )}
      {errorMessage && (
        <Form.Text className="d-block text-danger mt-2">
          {errorMessage}
        </Form.Text>
      )}
      {!error && !isLoading && (
        <Form.Text className="d-block text-muted mt-2">
          <small>Start typing to search for locations</small>
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default LocationAutocomplete;
